<template>
  <a-select
    :value.trim="props.value"
    :mode="props.mode"
    :maxTagTextLength="30"
    :showSearch="props.showSearch"
    :allowClear="props.allowClear"
    :loading="isFetching"
    :disabled="props.isDisabled"
    :filterOption="false"
    :optionLabelProp="props.optionLabelProp"
    :placeholder="props.placeholder"
    :dropdownStyle="{ minWidth: '160px', maxHeight: '300px', overflow: 'hidden' }"
    :dropdownMenuStyle="{ maxHeight: '250px', overflow: 'auto' }"
    @change="onChange"
    @focus="onFocus"
    @blur="onBlur"
    @popupScroll="onScroll"
    @search="onSearch"
  >
    <template #dropdownRender="menu">
      <SelectNodes :vnodes="menu" />

      <div
        v-show="props.isLoading || isFetching"
        class="select-loader"
      />

      <slot name="extraButton" />
    </template>

    <slot
      name="options"
      :options="selectOptions"
    >
      <a-select-option
        v-for="option in selectOptions"
        :key="option[props.valueKey]"
        :value="option[props.valueKey]"
      >
        <slot
          name="option"
          :option="option"
        >
          {{ option[props.labelKey] }}
        </slot>
      </a-select-option>
    </slot>
  </a-select>
</template>

<script setup lang="ts">
import { computed, defineComponent, ref, shallowRef } from "vue"

type Props = {
  value?: string | string[]
  mode?: "tags" | "multiple" | "default"
  defaultOptions?: any[]
  isLoading?: boolean
  isDisabled?: boolean
  customRequest?: Function
  customRequestParams?: Record<string, string | number | null>
  valueKey?: string
  labelKey?: string
  searchKey?: string
  allowClear?: boolean
  placeholder?: string
  onFocusFetch?: boolean
  showSearch?: boolean
  optionLabelProp?: string
}
type Emits = {
  (e: "change", value: string | string[]): void
  (e: "focus"): void
  (e: "blur"): void
  (e: "scroll"): void
  (e: "search", searchValue?: string): void
}

const SelectNodes = defineComponent({
  props: {
    vnodes: {
      type: Object,
      required: true
    }
  },
  render() {
    return this.vnodes
  }
})

const props = withDefaults(defineProps<Props>(), {
  mode: "default",
  defaultOptions: () => [],
  isLoading: false,
  isDisabled: false,
  customRequestParams: () => ({}),
  valueKey: "oid",
  labelKey: "name",
  searchKey: "",
  allowClear: true,
  placeholder: "",
  onFocusFetch: false,
  showSearch: true,
  optionLabelProp: "children"
})
const emit = defineEmits<Emits>()

const abortController = ref<AbortController>()
const searchTimeout = ref()

const onChange = (value) => {
  emit("change", value)
}
const onFocus = () => {
  emit("focus")

  if (!props.onFocusFetch || dataSource.value.length) return

  handleSearchOptions()
}
const onBlur = () => {
  lastSearchedValue.value = ""
  emit("blur")
}

const isFetching = shallowRef<boolean>(false)
const lastSearchedValue = ref<string>("")
const dataSource = ref([])
const dataSourceCount = ref<number>()

const selectOptions = computed(() => {
  if (!props.defaultOptions.length) return dataSource.value

  const seenKeys = new Set(props.defaultOptions.map((opt) => opt[props.valueKey]))
  const mergedOptions = [
    ...props.defaultOptions,
    ...dataSource.value.filter((item) => {
      if (seenKeys.has(item[props.valueKey])) return false

      seenKeys.add(item[props.valueKey])
      return true
    })
  ]
  return mergedOptions
})

const onResetOptions = () => {
  dataSource.value.length = 0
  dataSourceCount.value = undefined
}
const onSearch = (value: string = "") => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value)
    searchTimeout.value = undefined
  }

  lastSearchedValue.value = value.trim()

  searchTimeout.value = setTimeout(() => {
    if (!props.customRequest) return emit("search", lastSearchedValue.value)

    onResetOptions()

    handleSearchOptions()

    searchTimeout.value = undefined
  }, 500)
}
const handleSearchOptions = async () => {
  if (!props.customRequest) return

  try {
    if (abortController.value) {
      abortController.value.abort()
      abortController.value = undefined
    }

    abortController.value = new AbortController()

    isFetching.value = true

    const searchKey = props.searchKey || props.labelKey

    const { data } = await props.customRequest({
      queryParams: {
        ...props.customRequestParams,
        [searchKey]: lastSearchedValue.value,
        pageSize: 10,
        page: Math.floor(dataSource.value.length / 10) + 1
      },
      signal: abortController.value.signal
    })

    dataSource.value.push(...data.results)
    dataSourceCount.value = data.count
    isFetching.value = false
  } catch (error) {
    if (error.message === "canceled") return

    isFetching.value = false
  }
}
const onScroll = async (event) => {
  if (event.target?.scrollHeight - event.target?.clientHeight > event.target?.scrollTop + 10) return

  if (!props.customRequest) return emit("scroll")

  if (isFetching.value) return

  if (selectOptions.value.length >= dataSourceCount.value) return

  handleSearchOptions()
}

defineExpose({
  selectOptions,
  onResetOptions
})
</script>

<style lang="scss" scoped>
.select-loader {
  width: 100%;
  height: 2px;
  position: absolute;
  left: 0;
  bottom: 60px;
  overflow: hidden;

  position: absolute;
  bottom: 0;
  left: 0;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 60%;
    background: linear-gradient(#40a9ff 0 0);
    will-change: transform;
    animation: l16 2s infinite;
  }
}
@keyframes l16 {
  0% {
    transform: translateX(-100%);
  }
  66% {
    transform: translateX(100%);
  }
  100% {
    transform: translateX(200%);
  }
}

.empty-slot {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;

  & > * {
    height: 72px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-flow: column nowrap;
  }
}
</style>
